// import { faGift } from "@jewlr/pro-light-svg-icons/faGift"
import { faStar } from "@jewlr/pro-regular-svg-icons/faStar"
import { faStar as solidStar } from "@jewlr/pro-solid-svg-icons/faStar"
import { faStarHalfAlt as solidStarHalf } from "@jewlr/pro-solid-svg-icons/faStarHalfAlt"
import PropTypes from "prop-types"
import { useCallback, useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { Link } from "react-router-dom"

import FontAwesomeIcon from "components/ui/font-awesome-icon"
import { IS_JEWLR, isTestMode } from "helpers/application"
import { setProductsClickedOnHistory } from "helpers/collection"
import { roundToHalf } from "helpers/round-to-half"

import Badge from "../badge"
import LooksSelector from "../looks-selector"

import ProductDescription from "./product-description"
import ProductImage from "./product-image"
import ProductPrice from "./product-price"
import WishlistIcon from "./wishlist-icon"

const CollectionProduct = ({
  confirmWishlist,
  index,
  newPageFocusRef,
  product,
  setProductParams,
}) => {
  const [selectedLook, setSelectedLook] = useState(setDefaultLook(product))

  const newPageStartIndex = useSelector(
    (state) => state.collection.newPageStartIndex
  )

  useEffect(() => {
    // Update selected metal when default_look is changed
    setSelectedLook(setDefaultLook(product))
  }, [product])

  const setClickedOnHistory = useCallback(() => {
    if (!isTestMode) {
      dataLayer.push({
        currency: sl.config.currency_code,
        event: "viewed-product-from-collection",
        metal: selectedLook,
        styleCode: product.rm_style_code || product.style_code,
      })
    }

    setProductsClickedOnHistory(product.rm_style_code || product.style_code, {
      cidx: index + 1,
      cpg: product.pageIndex || 1,
      csc: product.style_code,
    })
  }, [index, product, selectedLook])

  const selectedProductLook = product.looks[selectedLook]

  if (!selectedProductLook) {
    // Prevents selectedLook from being undefined and breaking the component on reload of products.
    // Gives useEffect a chance to update.
    return null
  }
  const badge = product.badges && product.badges[0]
  const showRatings =
    product.reviews &&
    product.reviews.total_review > 0 &&
    product.reviews.average_score >= 4

  return (
    <li className="relative">
      <Link
        aria-label={product.name}
        className="tablet:max-w-256 widescreen:max-w-300 mx-auto mb-8 block w-full max-w-full"
        data-cy={`collection-product-link-${product.style_code}`}
        data-lc="collection-product"
        onClick={setClickedOnHistory}
        ref={index === newPageStartIndex ? newPageFocusRef : undefined}
        to={selectedProductLook.product_url}
      >
        <ProductImage
          alt={product.name}
          images={selectedProductLook.images}
          index={index}
          styleCode={product.style_code}
          type={product.product_type}
        />
      </Link>

      {confirmWishlist && (
        <WishlistIcon
          {...{
            confirmWishlist,
            product,
            selectedProductLook,
            setProductParams,
          }}
        />
      )}
      {badge && IS_JEWLR() && (
        <Badge label={badge.label} variant={badge.variant} />
      )}
      {IS_JEWLR() && (
        <div
          className="text-12 desktop:my-20 mb-12 flex h-12 items-center gap-8"
          {...(showRatings && {
            "aria-label": `Rated ${product.reviews.average_score.toFixed(1)} out of 5 from ${product.reviews.total_review} reviews`,
          })}
        >
          {showRatings && (
            <>
              <span aria-hidden className="text-rating flex gap-2">
                {(() => {
                  const average_rating = roundToHalf(
                    product.reviews.average_score,
                    0.5
                  )
                  const starArray = []

                  for (let i = 1; i <= 5; i++) {
                    if (i <= average_rating) {
                      starArray.push(
                        <FontAwesomeIcon icon={solidStar} key={i} />
                      )
                    } else if (i <= average_rating + 0.5) {
                      starArray.push(
                        <FontAwesomeIcon icon={solidStarHalf} key={i} />
                      )
                    } else {
                      starArray.push(<FontAwesomeIcon icon={faStar} key={i} />)
                    }
                  }

                  return starArray
                })()}
              </span>
              <span className="text-14 leading-12 font-semibold">
                {product.reviews.total_review} Reviews
              </span>
            </>
          )}
        </div>
      )}
      {/* {sl.config.show_availability_in_collection &&
          product.isAvailForChristmas && (
            <Typography color="#197603" fontSize={11} my="4px">
              <FontAwesomeIcon
                icon={faGift}
                style={{ verticalAlign: "text-top" }}
              />{" "}
              Christmas Delivery
            </Typography>
          )} */}
      <ProductDescription productName={product.name} />
      {selectedProductLook.selling && (
        <ProductPrice
          product={{
            ...selectedProductLook,
            style_code: product.style_code,
          }}
        />
      )}
      {product.hasDiamond && (
        <span className="text-13 desktop:text-16 italic">
          {product.hasDiamond}
        </span>
      )}
      <LooksSelector
        className="desktop:pt-12 justify-start pb-0 pt-8"
        looks={product.looks}
        product={product}
        selectedLook={selectedLook}
        updateLook={(look) => setSelectedLook(look)}
      />
    </li>
  )
}

CollectionProduct.propTypes = {
  confirmWishlist: PropTypes.func,
  index: PropTypes.number,
  newPageFocusRef: PropTypes.object,
  product: PropTypes.object,
  setProductParams: PropTypes.func,
}

const setDefaultLook = (product) => {
  return Object.prototype.hasOwnProperty.call(
    product.looks,
    product.default_look
  )
    ? product.default_look
    : Object.keys(product.looks)[0]
}

export default CollectionProduct
